import {
  ENDPOINTS,
  ObligationOwnersRequestSchema,
  OutboundHealthRequestSchema,
  SearchWeaknessRequestSchema,
  StaleJudgementsRequestSchema,
  type ObligationOwnersRequest,
  type ObligationOwnersResponse,
  type OutboundHealthRequest,
  type OutboundHealthResponse,
  type SearchWeaknessRequest,
  type SearchWeaknessResponse,
  type StaleJudgementsRequest,
  type StaleJudgementsResponse,
} from '@clm/shared-types';

import { apiGet, type Query } from './client';

/**
 * `quality-search-weakness`, `quality-outbound-health`, `quality-obligation-owners`,
 * `quality-stale-judgements` — the CLM-40 quality dashboards, wrapped for the
 * browser.
 *
 * Same recipe as `clause-candidates.ts`: the path comes from the registry, the
 * request is `.parse`d before it leaves the browser (every request schema is
 * `.strict()`, so an invented or scope-shaped key is a ZodError here rather
 * than a parameter on the wire), and the response is **parsed** rather than
 * asserted so an off-contract answer is a defect to report, not data to show.
 *
 * Nothing here computes a number: every figure a panel renders is a wire
 * field, and the four wrappers never send `scope` or `roles` — scope is
 * resolved server-side from the token (NFR-06).
 *
 * Encoding for the GETs (`apps/api/src/http/decode.ts`): dotted keys for the
 * nested page object; `window_days`, `signal` and `status` as strings.
 * `undefined` is omitted, never sent as `""`.
 */

const WEAKNESS_ENDPOINT = ENDPOINTS['quality-search-weakness'];
const OUTBOUND_ENDPOINT = ENDPOINTS['quality-outbound-health'];
const OWNERS_ENDPOINT = ENDPOINTS['quality-obligation-owners'];
const STALE_ENDPOINT = ENDPOINTS['quality-stale-judgements'];

function pageQuery(request: {
  readonly page?: { readonly limit?: number | undefined; readonly cursor?: string | undefined } | undefined;
}): Query {
  const query: Record<string, string | number> = {};
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;
  return query;
}

/** Flatten a validated search-weakness request into the query the decoder expands. */
export function searchWeaknessQuery(request: SearchWeaknessRequest): Query {
  return {
    ...(request.window_days === undefined ? {} : { window_days: String(request.window_days) }),
    ...(request.signal === undefined ? {} : { signal: request.signal }),
    ...pageQuery(request),
  };
}

export function outboundHealthQuery(request: OutboundHealthRequest): Query {
  return {
    ...(request.window_days === undefined ? {} : { window_days: String(request.window_days) }),
    ...(request.status === undefined ? {} : { status: request.status }),
    ...pageQuery(request),
  };
}

export function obligationOwnersQuery(request: ObligationOwnersRequest): Query {
  return pageQuery(request);
}

export function staleJudgementsQuery(request: StaleJudgementsRequest): Query {
  return pageQuery(request);
}

export async function fetchSearchWeakness(
  request: SearchWeaknessRequest,
  signal?: AbortSignal,
): Promise<SearchWeaknessResponse> {
  const validated = SearchWeaknessRequestSchema.parse(request);
  const raw = await apiGet<unknown>(WEAKNESS_ENDPOINT.path, {
    query: searchWeaknessQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return WEAKNESS_ENDPOINT.response.parse(raw);
}

export async function fetchOutboundHealth(
  request: OutboundHealthRequest,
  signal?: AbortSignal,
): Promise<OutboundHealthResponse> {
  const validated = OutboundHealthRequestSchema.parse(request);
  const raw = await apiGet<unknown>(OUTBOUND_ENDPOINT.path, {
    query: outboundHealthQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return OUTBOUND_ENDPOINT.response.parse(raw);
}

export async function fetchObligationOwners(
  request: ObligationOwnersRequest,
  signal?: AbortSignal,
): Promise<ObligationOwnersResponse> {
  const validated = ObligationOwnersRequestSchema.parse(request);
  const raw = await apiGet<unknown>(OWNERS_ENDPOINT.path, {
    query: obligationOwnersQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return OWNERS_ENDPOINT.response.parse(raw);
}

export async function fetchStaleJudgements(
  request: StaleJudgementsRequest,
  signal?: AbortSignal,
): Promise<StaleJudgementsResponse> {
  const validated = StaleJudgementsRequestSchema.parse(request);
  const raw = await apiGet<unknown>(STALE_ENDPOINT.path, {
    query: staleJudgementsQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return STALE_ENDPOINT.response.parse(raw);
}
